/**
 * Clinical Notes Hooks
 * React Query hooks for doctor clinical notes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicalNotesApi, CreateClinicalNoteRequest, UpdateClinicalNoteRequest } from '../api/clinical-notes';
import { toast } from 'sonner';

/**
 * Hook to get all clinical notes for a patient
 */
export const useClinicalNotes = (patientId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['clinicalNotes', patientId],
    queryFn: () => clinicalNotesApi.getClinicalNotes(patientId),
    enabled: enabled && !!patientId,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Hook to create a new clinical note
 */
export const useCreateClinicalNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateClinicalNoteRequest) => clinicalNotesApi.createClinicalNote(data),
    onSuccess: (data, variables) => {
      toast.success('Clinical note saved successfully');
      // Invalidate and refetch notes for this patient
      queryClient.invalidateQueries({ queryKey: ['clinicalNotes', variables.patient_id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to save clinical note');
    },
  });
};

/**
 * Hook to update an existing clinical note
 */
export const useUpdateClinicalNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ noteId, data }: { noteId: number; data: UpdateClinicalNoteRequest; patientId: number }) => 
      clinicalNotesApi.updateClinicalNote(noteId, data),
    onSuccess: (data, variables) => {
      toast.success('Clinical note updated successfully');
      // Invalidate and refetch notes for this patient
      queryClient.invalidateQueries({ queryKey: ['clinicalNotes', variables.patientId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update clinical note');
    },
  });
};

/**
 * Hook to delete a clinical note
 */
export const useDeleteClinicalNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ noteId, patientId }: { noteId: number; patientId: number }) => 
      clinicalNotesApi.deleteClinicalNote(noteId),
    onSuccess: (data, variables) => {
      toast.success('Clinical note deleted successfully');
      // Invalidate and refetch notes for this patient
      queryClient.invalidateQueries({ queryKey: ['clinicalNotes', variables.patientId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete clinical note');
    },
  });
};
